import React, { useState, useEffect } from 'react';

type Option = {
  id: number;
  name: string;
}

type ButtonGroupProps = {
  options: Option[];
  variant?: 'primary' | 'secondary';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  onChange?: (selectedOption: Option) => void;
  className?: string;
  backgroundColor?: string;
  textColor?: string;
  rounded?: 'none' | 'sm' | 'md' | 'lg' | 'full';
}

export const ButtonGroup: React.FC<ButtonGroupProps> = ({
  options,
  size = 'md',
  disabled = false,
  onChange,
  className = '',
  rounded = 'md',
}) => {
  const [selectedOption, setSelectedOption] = useState<Option>({ id: 0, name: '' });

  const sizeStyles = {
    sm: 'px-2 py-1 text-sm',
    md: 'px-3 py-1.5 text-sm',
    lg: 'px-6 py-3 text-lg'
  };

  const roundedStyles = {
    none: 'rounded-none',
    sm: 'rounded-sm',
    md: 'rounded-md',
    lg: 'rounded-lg',
    full: 'rounded-full'
  };

  const baseStyles = `
    ${sizeStyles[size]}
    ${roundedStyles[rounded]}
    transition-all
    duration-200
    whitespace-nowrap
    ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
  `;

  const buttonStyles = {
    container: 'flex overflow-x-auto w-full [&::-webkit-scrollbar]:hidden gap-1',
    selected: 'bg-primary-700 text-white hover:bg-primary-600',
    unselected: 'bg-secondary-600 text-white hover:bg-opacity-80',
    base: baseStyles
  };

  useEffect(() => {
    setSelectedOption(options[0]);
  }, [options]);

  const handleOptionClick = (option: Option) => {
    if (disabled) return;
    setSelectedOption(option);
    onChange?.(option);
  };

  const isSelected = (option: Option) => selectedOption?.id === option?.id;

  return (
    <div className={`${buttonStyles.container} ${className}`}>
      {options.map((option, index) => (
        <button
          key={index}
          onClick={() => handleOptionClick(option)}
          disabled={disabled}
          className={`
            ${buttonStyles.base}
            ${isSelected(option) ? buttonStyles.selected : buttonStyles.unselected}
          `}
          data-selected={isSelected(option)}
          data-testid={`group-button-${option.id}`}
          aria-pressed={isSelected(option)}
        >
          {option.name}
        </button>
      ))}
    </div>
  );
};